export type Prettify<T> = T extends unknown ? { [K in keyof T]: T[K] } : never;

export type Super<T extends U, U> = U extends T ? T : U;

export type ValueOf<T> = T extends readonly unknown[] ? T[number] : T[keyof T];

export type ExtendProps<P1, P2> = P1 & Omit<P2, keyof P1>;

// Make all properties mandatory, but preserve undefined,
// so we don't forget to forward optional properties
export type IncludeOptionals<T> = { [K in keyof Required<T>]: T[K] };

export type PseudoPartial<T> = IncludeOptionals<Partial<T>>;

export type FilterValues<T, V> = Pick<
  T,
  {
    [P in keyof T]: T[P] extends V ? P : never;
  }[keyof T]
>;

export type ExtractKeys<T, U> = {
  [K in keyof T]-?: Required<T>[K] extends U ? K : never;
}[keyof T];

declare const BRAND_SYMBOL: unique symbol;

export type Branded<Type, Brand> = Type & { [BRAND_SYMBOL]: Brand };

export type FixedLengthArray<
  T,
  Length extends number,
  Acc extends T[] = [],
> = number extends Length
  ? T[]
  : Acc['length'] extends Length
    ? Acc
    : FixedLengthArray<T, Length, [...Acc, T]>;

export type ReadonlyArrayOfMinLength<
  T,
  Length extends number,
  Acc extends readonly T[] = readonly [],
> = number extends Length
  ? readonly T[]
  : Acc['length'] extends Length
    ? readonly [...Acc, ...T[]]
    : ReadonlyArrayOfMinLength<T, Length, readonly [...Acc, T]>;

export type MinLength<
  Arr extends readonly unknown[],
  Acc extends readonly unknown[] = readonly [],
> = Arr extends readonly [infer Head, ...infer Tail]
  ? undefined extends Head
    ? Acc['length']
    : MinLength<Tail, readonly [...Acc, Head]>
  : Acc['length'];

export type Mutable<T> = {
  -readonly [K in keyof T]: T[K];
};

export type Primitive =
  | string
  | number
  | boolean
  | bigint
  | symbol
  | null
  | undefined;

// prettier-ignore
export type DeepMutable<T> =
  T extends Primitive ? T :
  T extends ReadonlySet<infer V> ? Set<DeepMutable<V>> :
  T extends ReadonlyMap<infer K, infer V> ? Map<K, DeepMutable<V>> :
  T extends (...args: readonly any[]) => any ? T :
  T extends object | readonly any[] ? { -readonly [K in keyof T]: DeepMutable<T[K]> } :
  T;

/**
 * Cast an object as deeply mutable.
 * I HOPE YOU KNOW WHAT YOU'RE DOING!
 */
export function unsafe_asDeepMutable<T>(value: T): DeepMutable<T> {
  return value as DeepMutable<T>;
}

/**
 * Cast an object as mutable.
 * I HOPE YOU KNOW WHAT YOU'RE DOING!
 */
export function unsafe_asMutable<T>(
  value: T extends object ? Readonly<T> : T,
): Mutable<T> {
  return value as Mutable<T>;
}

export function unsafe_asRequired<T>(
  value: T extends object ? Partial<T> : T,
): Required<T> {
  return value as Required<T>;
}

export type ReadonlyRecord<K extends keyof any, V> = {
  readonly [_ in K]: V;
};

type LooseUnionHelper<
  T,
  TUnion = T,
  TUnionKeys extends PropertyKey = TUnion extends NonNullable<unknown>
    ? keyof TUnion
    : never,
> =
  T extends NonNullable<unknown>
    ? Prettify<
        T & {
          [K in Exclude<TUnionKeys, keyof T>]?: TUnion extends {
            readonly [_ in K]?: infer V;
          }
            ? V
            : never;
        }
      >
    : T;

export type LooseUnion<T> = LooseUnionHelper<T>;

export function asLooseUnion<T>(value: T): LooseUnion<T> {
  return value as LooseUnion<T>;
}

export type NonEmptyArray<T> = [T, ...T[]];

export type SymbolKey = {
  [key in symbol]?: string;
};

export type ReadonlyFlatten<T> = T extends unknown
  ? { readonly [K in keyof T]: T[K] }
  : never;

export type TaggedUnion<
  TagConfig extends Record<PropertyKey, PropertyKey>,
  States extends Record<ValueOf<TagConfig>, unknown>,
> = {
  [Tags in keyof States]: ReadonlyFlatten<
    Record<keyof TagConfig, Tags> & States[Tags]
  >;
}[keyof States];
